import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useProducts,
  useCreateProduct,
  useUpdateProduct,
  useDeleteProduct,
  useImportProducts,
  useProductCategories,
  useSuppliers,
} from '../../../hooks/queries/useInventoryQueries';
import { useNotifications } from '../../../hooks/useNotifications';
import { useConfirm } from '../../../hooks/useConfirm';
import ConfirmDialog from '../../common/ConfirmDialog';
import ProductForm from './ProductForm';
import {
  exportProductsToCsv,
  parseProductsCsv,
  downloadProductTemplate,
} from '../../../utils/productCsv';
import '../inventory.css';
import './ProductForm.css';
import PmTable from '../../common/PmTable/PmTable';

const ProductsList = ({ organization }) => {
  const { t } = useTranslation();
  const { pushToast } = useNotifications();
  const { confirm, confirmDialogProps } = useConfirm();

  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategoryId, setFilterCategoryId] = useState('');
  const [filterType, setFilterType] = useState('');
  const [editingProduct, setEditingProduct] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [tableColumns, setTableColumns] = useState([]);
  const [columnSettings, setColumnSettings] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const [importPreview, setImportPreview] = useState(null);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const fileInputRef = useRef(null);

  const filters = useMemo(() => {
    const f = {};
    if (filterCategoryId) f.categoryId = filterCategoryId;
    if (filterType) f.type = filterType;
    return f;
  }, [filterCategoryId, filterType]);

  const {
    data: productsData,
    isLoading: loading,
    error: queryError,
    refetch,
  } = useProducts(organization, filters);

  const { data: categoriesData } = useProductCategories(organization);
  const { data: suppliersData } = useSuppliers(organization);

  const products = useMemo(() => {
    return Array.isArray(productsData) ? productsData : (productsData?.data || []);
  }, [productsData]);

  const categories = useMemo(() => {
    return Array.isArray(categoriesData) ? categoriesData : (categoriesData?.data || []);
  }, [categoriesData]);

  const suppliers = useMemo(() => {
    return Array.isArray(suppliersData) ? suppliersData : (suppliersData?.data || []);
  }, [suppliersData]);

  const brands = []; // brands endpoint not available yet

  const createProduct = useCreateProduct(organization);
  const updateProduct = useUpdateProduct(organization);
  const deleteProduct = useDeleteProduct(organization);
  const importProducts = useImportProducts(organization);

  // Load column settings from localStorage
  useEffect(() => {
    if (!organization) return;
    const saved = localStorage.getItem(`productColumns_${organization}`);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setColumnSettings(parsed.settings || {});
        setSortConfig(parsed.sort || { key: null, direction: 'asc' });
      } catch (e) {
        console.error('Error loading column settings:', e);
      }
    }
  }, [organization]);

  // Save column settings to localStorage
  const saveColumnSettings = (settings, sort) => {
    localStorage.setItem(`productColumns_${organization}`, JSON.stringify({
      settings,
      sort,
    }));
  };

  // Generate dynamic columns when products change
  useEffect(() => {
    if (products.length === 0) {
      setTableColumns([]);
      return;
    }
    const saved = localStorage.getItem(`productColumns_${organization}`);
    let savedSettings = {};
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        savedSettings = parsed.settings || {};
      } catch (e) {
        console.error('Error parsing saved settings:', e);
      }
    }

    const allKnownColumns = [
      { key: 'name', label: 'Name' },
      { key: 'sku', label: 'Sku' },
      { key: 'productType', label: 'Product Type' },
      { key: 'categoryId', label: 'Category Id' },
      { key: 'supplierId', label: 'Supplier Id' },
      { key: 'brandId', label: 'Brand Id' },
      { key: 'unitOfMeasure', label: 'Unit Of Measure' },
      { key: 'salesPrice', label: 'Sales Price' },
      { key: 'cost', label: 'Cost' },
      { key: 'quantityOnHand', label: 'Quantity On Hand' },
      { key: 'canBeSold', label: 'Can Be Sold' },
      { key: 'trackInventory', label: 'Track Inventory' },
      { key: 'isFavorite', label: 'Is Favorite' },
      { key: 'description', label: 'Description' },
      { key: 'reference', label: 'Reference' },
      { key: 'barcode', label: 'Barcode' },
      { key: 'internalNotes', label: 'Internal Notes' },
      { key: 'responsible', label: 'Responsible' },
      { key: 'weight', label: 'Weight' },
      { key: 'volume', label: 'Volume' },
      { key: 'customerLeadTime', label: 'Customer Lead Time' },
      { key: 'descriptionReceipts', label: 'Description Receipts' },
      { key: 'descriptionDelivery', label: 'Description Delivery' },
    ];
    const csvKeySet = new Set(allKnownColumns.map(c => c.key));

    const apiKeys = products.reduce((keys, p) => {
      Object.keys(p).forEach(k => {
        if (k !== 'id' && k !== 'organization' && !csvKeySet.has(k)) keys.add(k);
      });
      return keys;
    }, new Set());

    const columns = [
      ...allKnownColumns.map(col => ({
        key: col.key,
        label: col.label,
        visible: savedSettings[col.key]?.visible !== false,
        order: savedSettings[col.key]?.order ?? allKnownColumns.indexOf(col),
      })),
      ...[...apiKeys].map((key, i) => ({
        key,
        label: formatColumnLabel(key),
        visible: savedSettings[key]?.visible !== false,
        order: savedSettings[key]?.order ?? 999 + i,
      })),
    ];
    columns.sort((a, b) => a.order - b.order);
    setTableColumns(columns);
  }, [products, organization]);

  // Toggle column visibility
  const toggleColumnVisibility = (key) => {
    const newSettings = {
      ...columnSettings,
      [key]: {
        ...columnSettings[key],
        visible: !(columnSettings[key]?.visible ?? true),
      },
    };
    setColumnSettings(newSettings);
    saveColumnSettings(newSettings, sortConfig);
    setTableColumns(prev => prev.map(col =>
      col.key === key ? { ...col, visible: !col.visible } : col
    ));
  };

  // Move column up
  const moveColumnUp = (key) => {
    const currentIndex = tableColumns.findIndex(col => col.key === key);
    if (currentIndex <= 0) return;
    const newColumns = [...tableColumns];
    [newColumns[currentIndex - 1], newColumns[currentIndex]] =
      [newColumns[currentIndex], newColumns[currentIndex - 1]];

    const newSettings = { ...columnSettings };
    newColumns.forEach((col, index) => {
      newSettings[col.key] = { ...newSettings[col.key], order: index };
    });

    setColumnSettings(newSettings);
    saveColumnSettings(newSettings, sortConfig);
    setTableColumns(newColumns);
  };

  // Move column down
  const moveColumnDown = (key) => {
    const currentIndex = tableColumns.findIndex(col => col.key === key);
    if (currentIndex >= tableColumns.length - 1) return;
    const newColumns = [...tableColumns];
    [newColumns[currentIndex], newColumns[currentIndex + 1]] =
      [newColumns[currentIndex + 1], newColumns[currentIndex]];

    const newSettings = { ...columnSettings };
    newColumns.forEach((col, index) => {
      newSettings[col.key] = { ...newSettings[col.key], order: index };
    });

    setColumnSettings(newSettings);
    saveColumnSettings(newSettings, sortConfig);
    setTableColumns(newColumns);
  };

  // Handle sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    const newSortConfig = { key, direction };
    setSortConfig(newSortConfig);
    saveColumnSettings(columnSettings, newSortConfig);
  };

  // Sort products
  const sortedProducts = useMemo(() => {
    let sortable = [...products];
    if (sortConfig.key) {
      sortable.sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];
        if (aVal === null || aVal === undefined) return 1;
        if (bVal === null || bVal === undefined) return -1;
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return sortable;
  }, [products, sortConfig]);

  const filteredProducts = useMemo(() => {
    if (!searchTerm) return sortedProducts;
    const term = searchTerm.toLowerCase();
    return sortedProducts.filter(product =>
      Object.values(product).some(value =>
        value?.toString().toLowerCase().includes(term)
      )
    );
  }, [sortedProducts, searchTerm]);

  const visibleColumns = tableColumns.filter(col => col.visible);

  const handleReorderColumns = useCallback((orderedKeys) => {
    const newColumns = orderedKeys.map((key, idx) => {
      const existing = tableColumns.find(c => c.key === key);
      return { ...existing, order: idx };
    });
    const newSettings = {};
    newColumns.forEach((col, idx) => {
      newSettings[col.key] = {
        visible: col.visible !== false,
        order: idx,
      };
    });
    setColumnSettings(newSettings);
    saveColumnSettings(newSettings, sortConfig);
    setTableColumns(newColumns);
  }, [tableColumns, sortConfig]);

  const resetColumnSettings = () => {
    localStorage.removeItem(`productColumns_${organization}`);
    setColumnSettings({});
    setSortConfig({ key: null, direction: 'asc' });
    refetch();
  };

  const formatColumnLabel = (key) => {
    return key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, char => char.toUpperCase());
  };

  const formatCellValue = (key, value) => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'boolean') {
      return value ? 'Active' : 'Inactive';
    }
    if ((key.includes('price') || key.includes('cost')) && typeof value === 'number') {
      return `$${value.toFixed(2)}`;
    }
    if (key.includes('date') || key.includes('created') || key.includes('updated')) {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString();
        }
      } catch (e) {
        return value;
      }
    }
    return value.toString();
  };

  // Helper function to build category hierarchy for display
  const buildCategoryHierarchy = (cats) => {
    const categoryMap = {};
    cats.forEach(cat => {
      categoryMap[cat.id] = { ...cat, children: [] };
    });
    const rootCategories = [];
    cats.forEach(cat => {
      if (cat.parentId) {
        const parent = categoryMap[cat.parentId];
        if (parent) {
          parent.children.push(categoryMap[cat.id]);
        } else {
          rootCategories.push(categoryMap[cat.id]);
        }
      } else {
        rootCategories.push(categoryMap[cat.id]);
      }
    });
    const flattenWithIndent = (cats, level = 0) => {
      let result = [];
      cats.forEach(cat => {
        const indent = '—'.repeat(level);
        result.push({
          value: cat.id,
          label: level > 0 ? `${indent} ${cat.name || cat.categoryName}` : cat.name || cat.categoryName,
        });
        if (cat.children && cat.children.length > 0) {
          result = result.concat(flattenWithIndent(cat.children, level + 1));
        }
      });
      return result;
    };
    return flattenWithIndent(rootCategories);
  };

  const handleOpenModal = (product = null) => {
    setEditingProduct(product);
    setIsFormOpen(true);
  };

  const handleCloseModal = () => {
    setIsFormOpen(false);
    setEditingProduct(null);
  };

  const handleSave = async (productData) => {
    try {
      const payload = { ...productData, organization };
      if (editingProduct?.id) {
        await updateProduct.mutateAsync({ id: editingProduct.id, ...payload });
        pushToast({ type: 'success', title: t('inventory.edit_product_title'), body: t('success') });
      } else {
        await createProduct.mutateAsync(payload);
        pushToast({ type: 'success', title: t('inventory.add_product_title'), body: t('success') });
      }
      handleCloseModal();
    } catch (err) {
      pushToast({ type: 'error', title: t('inventory.product_form.error_saving'), body: err.message || t('error') });
    }
  };

  const handleDelete = async (productId) => {
    const confirmed = await confirm({
      title: t('inventory.delete_product_confirm'),
      message: t('inventory.delete_product_confirm'),
      confirmLabel: t('delete'),
      cancelLabel: t('cancel'),
      danger: true,
    });
    if (!confirmed) return;
    try {
      await deleteProduct.mutateAsync(productId);
      pushToast({ type: 'success', title: t('inventory.products_title'), body: t('success') });
    } catch (err) {
      pushToast({ type: 'error', title: t('error'), body: err.message || t('error') });
    }
  };

  const handleExportCsv = () => {
    if (!products || products.length === 0) {
      pushToast({ type: 'warning', title: t('inventory.products_title'), body: t('inventory.no_products_found') });
      return;
    }
    exportProductsToCsv(products);
    pushToast({ type: 'success', title: t('inventory.products_title'), body: t('inventory.product_form.export_csv_button') });
  };

  const handleImportFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const result = await parseProductsCsv(file);
      setImportPreview(result);
      setIsImportModalOpen(true);
    } catch (err) {
      pushToast({ type: 'error', title: t('error'), body: err.message || t('error') });
    }
    e.target.value = '';
  };

  const handleConfirmImport = async () => {
    if (!importPreview?.valid?.length) return;
    try {
      const result = await importProducts.mutateAsync({
        organization,
        products: importPreview.valid,
      });
      if (result.errors.length > 0) {
        pushToast({
          type: 'warning',
          title: t('inventory.product_form.import_preview_title'),
          body: `${result.succeeded}/${result.total} ${t('inventory.product_form.import_success')} — ${result.errors.length} errors`,
        });
      } else {
        pushToast({ type: 'success', title: t('inventory.product_form.import_preview_title'), body: t('inventory.product_form.import_success') });
      }
      setIsImportModalOpen(false);
      setImportPreview(null);
    } catch (err) {
      pushToast({ type: 'error', title: t('error'), body: err.message || t('error') });
    }
  };

  const handleCancelImport = () => {
    setIsImportModalOpen(false);
    setImportPreview(null);
  };

  const handleDownloadTemplate = () => {
    downloadProductTemplate();
    pushToast({ type: 'success', title: t('inventory.products_title'), body: t('inventory.product_form.download_template_button') });
  };

  if (loading) {
    return (
      <div className="pm-page-card pm-skeleton" aria-busy="true">
        {t('inventory.loading_products')}
      </div>
    );
  }

  if (queryError) {
    return (
      <div className="pm-page-card">
        <p style={{ color: 'var(--pico-del-color)' }}>
          {t('inventory.error_label')} {queryError.message || t('error')}
        </p>
        <button className="pm-btn-primary" onClick={() => refetch()}>
          {t('inventory.retry_button')}
        </button>
      </div>
    );
  }

  const categoryOptions = buildCategoryHierarchy(categories);

  return (
    <div className="pm-page-card">
      <div className="pm-header">
        <h2 className="pm-header-title">{t('inventory.products_title')}</h2>
        <div className="pm-header-actions">
          <button className="pm-btn-primary" onClick={() => handleOpenModal()}>
            <span aria-hidden="true">➕</span>
            <span>{t('inventory.add_product_button')}</span>
          </button>
          <div className="pm-btn-group">
            <button className="pm-btn-secondary" onClick={handleExportCsv} title={t('inventory.product_form.export_csv_button')}>
              <span aria-hidden="true">📤</span>
              <span className="desktop-only">{t('inventory.product_form.export_csv_button')}</span>
            </button>
            <button className="pm-btn-secondary" onClick={() => fileInputRef.current?.click()} title={t('inventory.product_form.import_csv_button')}>
              <span aria-hidden="true">📥</span>
              <span className="desktop-only">{t('inventory.product_form.import_csv_button')}</span>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              style={{ display: 'none' }}
              onChange={handleImportFileChange}
            />
            <button className="pm-btn-secondary" onClick={handleDownloadTemplate} title={t('inventory.product_form.download_template_button')}>
              <span aria-hidden="true">📄</span>
              <span className="desktop-only">{t('inventory.product_form.download_template_button')}</span>
            </button>
          </div>
        </div>
      </div>

      <div className="pm-toolbar">
        <div className="pm-search-wrap">
          <input
            type="search"
            placeholder={t('inventory.search_products_placeholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="pm-filter-wrap">
          <select
            value={filterCategoryId}
            onChange={(e) => setFilterCategoryId(e.target.value)}
          >
            <option value="">{t('inventory.all_products_option')}</option>
            {categoryOptions.map(cat => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="">{t('inventory.all_types_option')}</option>
            <option value="goods">{t('inventory.product_form.type_goods')}</option>
            <option value="service">{t('inventory.product_form.type_service')}</option>
            <option value="combo">{t('inventory.product_form.type_combo')}</option>
          </select>
        </div>
      </div>

      <PmTable
        columns={tableColumns}
        data={filteredProducts}
        onSort={handleSort}
        sortConfig={sortConfig}
        onToggleColumn={toggleColumnVisibility}
        onReorderColumns={handleReorderColumns}
        onResetColumns={resetColumnSettings}
        formatCellValue={formatCellValue}
        renderActions={(product) => (
          <>
            <button
              className="pm-row-btn edit"
              onClick={() => handleOpenModal(product)}
              title={t('inventory.edit_tooltip')}
            >
              ✏️
            </button>
            <button
              className="pm-row-btn delete"
              onClick={() => handleDelete(product.id)}
              title={t('inventory.delete_tooltip')}
            >
              🗑️
            </button>
          </>
        )}
        isLoading={loading}
        emptyMessage={t('inventory.no_products_found')}
      />

      {isFormOpen && (
        <ProductForm
          initialData={editingProduct}
          categories={categories}
          suppliers={suppliers}
          brands={brands}
          onSave={handleSave}
          onCancel={handleCloseModal}
          saving={createProduct.isPending || updateProduct.isPending}
          error={createProduct.error?.message || updateProduct.error?.message || null}
        />
      )}

      {isImportModalOpen && importPreview && (
        <div className="pm-modal-overlay">
          <div className="pm-modal-content" style={{ maxWidth: '900px' }}>
            <div className="pm-modal-header">
              <h2>{t('inventory.product_form.import_preview_title')}</h2>
              <button className="pm-modal-close" onClick={handleCancelImport}>&times;</button>
            </div>
            <div className="pm-modal-body">
              <div className="pm-import-summary">
                <span className="pm-badge valid">
                  {importPreview.valid.length} {t('inventory.badge_valid')}
                </span>
                {importPreview.errors.length > 0 && (
                  <span className="pm-badge error">
                    {importPreview.errors.length} {t('inventory.badge_errors')}
                  </span>
                )}
              </div>

              {importPreview.errors.length > 0 && (
                <div className="pm-import-errors">
                  <strong>{t('inventory.product_form.import_errors_found', { count: importPreview.errors.length })}</strong>
                  <ul>
                    {importPreview.errors.map((err, idx) => (
                      <li key={idx}>Row {err.row}: {err.message}</li>
                    ))}
                  </ul>
                </div>
              )}

              {importPreview.valid.length > 0 ? (
                <div className="pm-import-table-wrap">
                  <table className="pm-import-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>{t('inventory.product_form.field_name')}</th>
                        <th>SKU</th>
                        <th>{t('inventory.product_form.field_productType')}</th>
                        <th>{t('inventory.product_form.field_salesPrice')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importPreview.valid.map((row, idx) => (
                        <tr key={idx}>
                          <td>{idx + 1}</td>
                          <td>{row.name}</td>
                          <td>{row.sku || '-'}</td>
                          <td>{row.productType || '-'}</td>
                          <td>{row.salesPrice != null ? row.salesPrice : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="pm-empty-state">{t('inventory.no_products_found')}</p>
              )}
            </div>
            <div className="pm-modal-footer">
              <button className="pm-btn-secondary" onClick={handleCancelImport}>
                {t('inventory.product_form.import_preview_cancel')}
              </button>
              <button
                className="pm-btn-primary"
                onClick={handleConfirmImport}
                disabled={importPreview.valid.length === 0 || importProducts.isPending}
                aria-busy={importProducts.isPending ? 'true' : undefined}
              >
                {importProducts.isPending
                  ? t('inventory.product_form.saving_button')
                  : t('inventory.product_form.import_preview_confirm', { count: importPreview.valid.length })}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmDialogProps.open}
        title={confirmDialogProps.title}
        message={confirmDialogProps.message}
        confirmLabel={confirmDialogProps.confirmLabel}
        cancelLabel={confirmDialogProps.cancelLabel}
        danger={confirmDialogProps.danger}
        onConfirm={confirmDialogProps.onConfirm}
        onCancel={confirmDialogProps.onCancel}
      />
    </div>
  );
};

export default ProductsList;
